<script setup>
import { ref, computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useContactStore } from '@/stores/contact';
import { useOptionsStore } from '@/stores/options';
import { useFoodflowSessionStore } from '@/stores/foodflowSession';
import { categoryIcon } from '@/utils/foodCategoryIcons';
import { searchContacts, sortContactsByName, isDarkColor } from '@/utils/contactSearch';
import { trans } from 'laravel-vue-i18n';
import ScaleConnectionStatus from '@/components/FoodFlow/ScaleConnectionStatus.vue';
import { useScaleAutoFill } from '@/composables/scaleReader';

const emit = defineEmits(['close']);

const { active: activeContacts } = storeToRefs(useContactStore());
const { foodCategoryOptions } = storeToRefs(useOptionsStore());
const { storeSession } = useFoodflowSessionStore();

// step: 'type' | 'contact' | 'entry' | 'success'
const step = ref('type');
const sessionType = ref(null);
const selectedContact = ref(null);
const searchTerm = ref('');
const lookupError = ref('');

const selectedCategoryId = ref(null);
const weight = ref('');
const entries = ref([]);
const sessionNotes = ref('');
const lastSummary = ref(null);
const submitting = ref(false);
const weightInput = ref(null);

const { isSettling } = useScaleAutoFill(weight);

const focusWeightInput = () => {
    nextTick(() => weightInput.value?.$el?.querySelector('input')?.focus());
};

const isIntake = computed(() => sessionType.value === 'intake');
const contactHeading = computed(() => isIntake.value ? trans('label.select_supplier') : trans('label.select_recipient'));

const barColorClass = computed(() => {
    if (!sessionType.value) {
        return 'bg-[#263a52]';
    }
    return isIntake.value ? 'bg-[#1d3d24]' : 'bg-[#3d1e1e]';
});

const weightColorClass = computed(() => isIntake.value ? 'text-[#2d6b1a]' : 'text-[#a83430]');

const addButtonClass = computed(() => {
    if (!canAdd.value) {
        return 'cursor-not-allowed bg-[#EDE8DF] text-[#94a3b8]';
    }
    return isIntake.value ? 'bg-[#61B245] hover:bg-[#4a8a33]' : 'bg-[#F06660] hover:bg-[#d44a44]';
});

const contactsForType = computed(() => {
    if (!activeContacts.value || !sessionType.value) {
        return [];
    }
    return activeContacts.value.filter(c => isIntake.value
        ? ['supplier', 'both'].includes(c.role)
        : ['recipient', 'both'].includes(c.role));
});

const isSearching = computed(() => searchTerm.value.trim() !== '');

const filteredContacts = computed(() => {
    if (!isSearching.value) {
        return sortContactsByName(contactsForType.value.filter(c => c.is_preferred));
    }
    return searchContacts(contactsForType.value, searchTerm.value);
});

const contactTileStyle = (contact) => contact.color ? { backgroundColor: contact.color } : null;

const totalWeight = computed(() => entries.value.reduce((sum, e) => sum + e.weight, 0));
const canAdd = computed(() => selectedCategoryId.value && parseFloat(weight.value) > 0 && !isSettling.value);
const canEndSession = computed(() => entries.value.length > 0);
const weightSign = computed(() => isIntake.value ? '+' : '-');

const selectType = (type) => {
    sessionType.value = type;
    searchTerm.value = '';
    lookupError.value = '';
    step.value = 'contact';
};

const changeType = () => {
    sessionType.value = null;
    step.value = 'type';
};

const selectContact = (contact) => {
    selectedContact.value = contact;
    entries.value = [];
    sessionNotes.value = '';
    selectedCategoryId.value = null;
    weight.value = '';
    step.value = 'entry';
};

const changeContact = () => {
    selectedContact.value = null;
    entries.value = [];
    searchTerm.value = '';
    lookupError.value = '';
    step.value = 'contact';
};

const attemptQrLookup = async () => {
    const value = searchTerm.value.trim();
    if (!value) {
        return;
    }

    const localMatch = contactsForType.value.find(c => c.hash === value);
    if (localMatch) {
        selectContact(localMatch);
        return;
    }

    lookupError.value = '';
    try {
        const contactStore = useContactStore();
        const response = await contactStore.qrLookup(value, { type: sessionType.value });
        selectContact(response.data.contact);
    } catch (error) {
        lookupError.value = trans('validation.custom.contact_hash.invalid_role');
    }
};

const selectCategory = (id) => {
    selectedCategoryId.value = id;
    focusWeightInput();
};

const addEntry = () => {
    if (!canAdd.value) {
        return;
    }
    const category = foodCategoryOptions.value.find(c => c.id === selectedCategoryId.value);
    entries.value.push({
        food_category_id: selectedCategoryId.value,
        category_name: category?.name ?? '',
        weight: parseFloat(weight.value),
    });
    weight.value = '';
    selectedCategoryId.value = null;
    focusWeightInput();
};

const removeEntry = (index) => {
    entries.value.splice(index, 1);
};

const endSession = async () => {
    if (!canEndSession.value || submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        await storeSession({
            type: sessionType.value,
            contact_hash: selectedContact.value.hash,
            notes: sessionNotes.value.trim() || null,
            entries: entries.value.map(e => ({
                food_category_id: e.food_category_id,
                weight: e.weight,
            })),
        });

        lastSummary.value = {
            contactName: selectedContact.value.name,
            type: sessionType.value,
            entryCount: entries.value.length,
            totalWeight: totalWeight.value,
        };
        step.value = 'success';
    } catch (error) {
        // Validation and server errors are surfaced centrally via the error store and toast interceptors.
    } finally {
        submitting.value = false;
    }
};

const startNext = () => {
    selectedContact.value = null;
    entries.value = [];
    searchTerm.value = '';
    lookupError.value = '';
    step.value = 'contact';
};

const close = () => emit('close');
</script>

<template>
<div class="wizard-shell">
    <div class="wizard-bar" :class="barColorClass">
        <button type="button" class="wizard-back-btn" @click="close">
            <FontAwesomeIcon icon="arrow-left" />{{ $t('label.back') }}
        </button>
        <div class="wizard-bar-divider" />
        <span class="wizard-bar-title">{{ $t('label.new_session') }}</span>
        <div v-if="sessionType" class="wizard-bar-type">
            <div class="h-5 w-px bg-white/20" />
            <div class="h-2 w-2 flex-shrink-0 rounded-full" :class="isIntake ? 'bg-[#61B245]' : 'bg-[#F06660]'" />
            <span class="wizard-bar-type-label">{{ isIntake ? $t('label.intake') : $t('label.outtake') }}</span>
        </div>
        <div v-if="sessionType" class="ml-auto flex-shrink-0 pl-3">
            <button type="button" class="session-type-button" @click="changeType">{{ $t('label.change_type') }}</button>
        </div>
    </div>

    <!-- Step: choose type -->
    <div v-if="step === 'type'" class="wizard-step-type">
        <div class="wizard-type-heading">{{ $t('label.what_are_you_logging') }}</div>
        <div class="wizard-type-grid">
            <button type="button" class="wizard-type-btn bg-[#dcf5d4] text-[#2d6b1a]" @click="selectType('intake')">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div class="text-[40px]">📥</div>
                <div class="text-[18px] font-bold">{{ $t('label.intake') }}</div>
                <div class="text-[13px] opacity-75">{{ $t('label.receiving_food') }}</div>
            </button>
            <button type="button" class="wizard-type-btn bg-[#fde8e7] text-[#a83430]" @click="selectType('outtake')">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div class="text-[40px]">📤</div>
                <div class="text-[18px] font-bold">{{ $t('label.outtake') }}</div>
                <div class="text-[13px] opacity-75">{{ $t('label.distributing_food') }}</div>
            </button>
        </div>
    </div>

    <!-- Step: choose contact -->
    <div v-else-if="step === 'contact'" class="wizard-step">
        <div class="wizard-step-header">
            <h2 class="wizard-step-title">{{ contactHeading }}</h2>
            <p class="text-[13px] text-[#64748b]">{{ $t('label.search_or_scan') }}</p>
        </div>
        <div class="wizard-search-bar">
            <div class="relative">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <span class="wizard-search-icon">🔍</span>
                <input
                    v-model="searchTerm"
                    type="text"
                    :placeholder="$t('label.search_or_scan')"
                    class="wizard-search-input"
                    @keyup.enter="attemptQrLookup">
                <button type="button" class="wizard-scan-btn" @click="attemptQrLookup">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"><rect
                            x="1"
                            y="1"
                            width="5"
                            height="5"
                            rx=".5"
                            stroke="white"
                            stroke-width="1.5"
                            fill="none" /><rect
                                x="2.5"
                                y="2.5"
                                width="2"
                                height="2"
                                fill="white" /><rect
                                    x="10"
                                    y="1"
                                    width="5"
                                    height="5"
                                    rx=".5"
                                    stroke="white"
                                    stroke-width="1.5"
                                    fill="none" /><rect
                                        x="11.5"
                                        y="2.5"
                                        width="2"
                                        height="2"
                                        fill="white" /><rect
                                            x="1"
                                            y="10"
                                            width="5"
                                            height="5"
                                            rx=".5"
                                            stroke="white"
                                            stroke-width="1.5"
                                            fill="none" /><rect
                                                x="2.5"
                                                y="11.5"
                                                width="2"
                                                height="2"
                                                fill="white" /><rect
                                                    x="10"
                                                    y="10"
                                                    width="2"
                                                    height="2"
                                                    fill="white" /><rect
                                                        x="13"
                                                        y="10"
                                                        width="2"
                                                        height="2"
                                                        fill="white" /><rect
                                                            x="10"
                                                            y="13"
                                                            width="2"
                                                            height="2"
                                                            fill="white" /><rect
                                                                x="13"
                                                                y="13"
                                                                width="2"
                                                                height="2"
                                                                fill="white" /></svg>
                    {{ $t('label.scan') }}
                </button>
            </div>
            <div v-if="lookupError" class="wizard-lookup-error">{{ lookupError }}</div>
        </div>
        <div class="wizard-contact-results">
            <div v-if="filteredContacts.length === 0" class="wizard-no-matches">
                {{ isSearching ? $t('label.no_matches_found') : $t('label.no_preferred_contacts') }}
            </div>
            <div v-else class="wizard-contact-grid">
                <div
                    v-for="contact in filteredContacts"
                    :key="contact.hash"
                    class="wizard-contact-card"
                    :class="{ 'is-dark': isDarkColor(contact.color) }"
                    :style="contactTileStyle(contact)"
                    @click="selectContact(contact)">
                    <div class="wizard-contact-card-name">{{ contact.name }}</div>
                    <div v-if="contact.type" class="wizard-contact-card-type">{{ contact.type.name }}</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Step: entries -->
    <div v-else-if="step === 'entry'" class="wizard-step">
        <div class="wizard-entry-header">
            <div class="wizard-contact-banner">
                <div>
                    <div class="wizard-contact-banner-name">{{ selectedContact?.name }}</div>
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    <div class="wizard-contact-banner-subtitle">{{ isIntake ? $t('label.intake') : $t('label.outtake') }} · {{ selectedContact?.type?.name }}</div>
                </div>
                <div class="flex items-center gap-3">
                    <ScaleConnectionStatus />
                    <button type="button" class="wizard-change-btn" @click="changeContact">{{ $t('label.change') }}</button>
                </div>
            </div>
        </div>
        <div class="wizard-entry-grid">
            <div class="wizard-entry-pane-left">
                <div class="wizard-field-label mb-2">{{ $t('label.category') }}</div>
                <div class="wizard-category-grid">
                    <div
                        v-for="category in foodCategoryOptions"
                        :key="category.id"
                        class="wizard-category-card bg-white"
                        :class="selectedCategoryId === category.id ? 'border-[#375475] bg-[#e7edf4]' : 'border-[#EDE8DF] hover:border-[#375475]'"
                        @click="selectCategory(category.id)">
                        <div class="mb-1 text-[20px]">{{ categoryIcon(category.name) }}</div>
                        <div class="wizard-category-card-name">{{ category.name }}</div>
                    </div>
                </div>
                <div class="wizard-field-label mb-2 mt-3">{{ $t('label.weight') }}</div>
                <div class="flex gap-2.5">
                    <div class="relative flex-1">
                        <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
                        <input
                            ref="weightInput"
                            v-model="weight"
                            type="number"
                            placeholder="0.0"
                            min="0"
                            step="0.1"
                            inputmode="decimal"
                            class="wizard-weight-input">
                        <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
                        <span class="wizard-weight-unit">{{ $t('label.lbs') }}</span>
                    </div>
                    <button
                        type="button"
                        class="wizard-add-btn text-white"
                        :class="addButtonClass"
                        :disabled="!canAdd"
                        @click="addEntry">{{ $t('label.add') }}</button>
                </div>
            </div>

            <div class="wizard-entry-pane-right">
                <div class="wizard-field-label mb-2 flex justify-between">
                    <span>{{ $t('label.this_session') }}</span>
                    <span v-if="entries.length">{{ entries.length }} {{ entries.length === 1 ? $t('label.entry') : $t('label.entries') }}</span>
                </div>
                <div class="flex-1 md:overflow-y-auto">
                    <div v-if="entries.length === 0" class="wizard-no-entries">
                        {{ $t('label.no_entries_yet') }}
                    </div>
                    <div v-for="(entry, index) in entries" :key="index" class="wizard-entry-row">
                        <div class="flex items-center gap-2.5">
                            <span class="text-[20px]">{{ categoryIcon(entry.category_name) }}</span>
                            <span class="text-[13px] font-semibold">{{ entry.category_name }}</span>
                        </div>
                        <div class="flex items-center gap-2.5">
                            <span class="text-[15px] font-bold" :class="weightColorClass">
                                {{ weightSign }}{{ entry.weight.toFixed(1) }} {{ $t('label.lbs') }}
                            </span>
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                            <button type="button" class="wizard-remove-entry-btn" @click="removeEntry(index)">✕</button>
                        </div>
                    </div>
                </div>
                <div v-if="entries.length" class="wizard-total-row">
                    <span class="wizard-total-label">{{ $t('label.total') }}</span>
                    <span class="text-[16px] font-bold" :class="weightColorClass">
                        {{ weightSign }}{{ totalWeight.toFixed(1) }} {{ $t('label.lbs') }}
                    </span>
                </div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <label for="session-notes" class="wizard-field-label mb-2 mt-3">{{ $t('label.notes') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
                <textarea
                    id="session-notes"
                    v-model="sessionNotes"
                    rows="2"
                    maxlength="1000"
                    class="foodflow-field-input resize-none" />
                <button
                    type="button"
                    class="wizard-end-session-btn"
                    :disabled="!canEndSession || submitting"
                    @click="endSession">
                    {{ $t('label.save_session') }}
                </button>
            </div>
        </div>
    </div>

    <!-- Step: success -->
    <div v-else-if="step === 'success'" class="wizard-step-success">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <div class="text-[56px]">&#x2705;</div>
        <div class="wizard-success-heading">{{ $t('label.session_saved') }}</div>
        <div class="wizard-summary-card">
            <div class="wizard-summary-row"><span class="text-right text-[#64748b]">{{ $t('label.supplier_recipient') }}</span><span class="wizard-summary-value">{{ lastSummary?.contactName }}</span></div>
            <div class="wizard-summary-row"><span class="text-right text-[#64748b]">{{ $t('label.type') }}</span><span class="wizard-summary-value capitalize">{{ lastSummary?.type === 'intake' ? $t('label.intake') : $t('label.outtake') }}</span></div>
            <div class="wizard-summary-row"><span class="text-right text-[#64748b]">{{ $t('label.entries') }}</span><span class="wizard-summary-value">{{ lastSummary?.entryCount }}</span></div>
            <div class="wizard-summary-row"><span class="text-right text-[#64748b]">{{ $t('label.total') }}</span><span class="wizard-summary-value">{{ weightSign }}{{ lastSummary?.totalWeight.toFixed(1) }} {{ $t('label.lbs') }}</span></div>
        </div>
        <button type="button" class="wizard-next-btn" @click="startNext">
            {{ isIntake ? $t('label.next_supplier') : $t('label.next_recipient') }}
        </button>
    </div>
</div>
</template>
